import React, { useEffect, useState } from 'react';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
import { useStyles } from './styles';
import LazyLoad from 'react-lazyload';
import useIsMobile from 'hooks/useIsMobile';
import Faq from 'components/Anniversary/Faq';
import CarouselAnniversary from '../Carousel';
import NextLink from 'next/link';
import { isUserFromJournal } from 'utils';
import { UserTypeEnum } from 'enums';
import ProfileService from 'services/ProfileService';
import { useAuthUser } from 'features/auth/AuthUserContext';
import dynamic from 'next/dynamic';
import CustomButton from 'components/CustomButton';
import { useRouter } from 'next/router';
import useGeoData from 'hooks/useGeoData';
import { geoDiscountConfig } from 'config/discount';

const DynamicFeatures = dynamic(() => import('components/Features'));
const DynamicTrustedByResearchers = dynamic(() => import('components/TrustedByResearchers'));

export default function MainContent() {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { user, isAuthenticated, profile } = useAuthUser();
  const userFromJournal = isUserFromJournal(profile);
  const [lastInvoice, setLastInvoice] = useState(null);
  const userIsResearcherMonth = lastInvoice?.billingInterval === 'month' && user?.tp_user === 'R';
  const userAvailableForPromotion =
    user?.tp_user === UserTypeEnum.TRIAL ||
    user?.tp_user === UserTypeEnum.STUDENT ||
    userIsResearcherMonth;
  const isJournalOrNotAvailableForPromotion =
    (isAuthenticated && !userAvailableForPromotion) || userFromJournal;
  const router = useRouter();
  const { geoData } = useGeoData();
  const countryCode = geoData?.country_code;
  const config = countryCode && (geoDiscountConfig[countryCode] || geoDiscountConfig.US);
  useEffect(() => {
    async function getLastInvoice() {
      const profileService = new ProfileService();
      const { data: lastInvoice } = await profileService.getLastInvoice();
      setLastInvoice(lastInvoice);
    }

    getLastInvoice();
  }, [user]);

  return (
    <>
      <LazyLoad offset={100}>
        <Box className={classes.dynamicBackground} bgcolor="#D5316B" pb={10} pt={10}>
          <Container>
            <Grid container={true} spacing={2} alignItems="center" justifyContent="center">
              <Grid item={true} xs={12} align="center">
                <div className={classes.contentCenter}>
                  <Typography
                    className={classes.mainHeading}
                    variant="h3"
                    component="h1"
                    color="white"
                  >
                    <b>11 years of Bringing Science to Life</b>
                  </Typography>

                  <Typography
                    className={classes.subHeading}
                    variant="h4"
                    component="h2"
                    color="white"
                  >
                    and we're just getting started!
                  </Typography>

                  <Box mt={3} mb={3}>
                    <Typography
                      className={classes.callToAction}
                      variant="h5"
                      component="p"
                      color="white"
                    >
                      Celebrate with us and get {config.discount}% off on your subscription!
                    </Typography>
                  </Box>
                  <NextLink
                    passHref={true}
                    href={{
                      pathname: '/pricing',
                      query: router.query,
                    }}
                  >
                    <Button
                      className={classes.subscribeButton}
                      variant="contained"
                      color="secondary"
                      size="small"
                    >
                      Subscribe now!
                    </Button>
                  </NextLink>
                </div>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </LazyLoad>

      <Box>
        <DynamicTrustedByResearchers
          bgColorProps={'#F8F8F8'}
          title={
            <>
              <span>Trusted by</span> researchers and academics from 100+ institutions
            </>
          }
          variantTitle={'h5'}
        />
      </Box>

      <DynamicFeatures />

      <LazyLoad offset={100}>
        <CarouselAnniversary
          isJournalOrNotAvailableForPromotion={isJournalOrNotAvailableForPromotion}
        />
      </LazyLoad>

      <LazyLoad offset={100}>
        {!isMobile && (
          <Container>
            <Box className={classes.mainBoxInformation} mx={7} mb={6}>
              <Typography className={classes.featuresBoxInformation} variant="h4" component="h2">
                A decade+ of Mind the Graph
              </Typography>

              <Box display="flex" justifyContent="left" mt={4}>
                <Typography className={classes.graphiteColor} variant="h6">
                  Join the celebration as we mark a decade of supporting scientific communications!
                  You're formally invited to join us in commemorating this milestone! Throughout
                  these remarkable 11 years, we've empowered{' '}
                  <b>over 400,000 scientists, academics and researchers</b> with a seamless and
                  user-friendly platform, enabling them to craft captivating science visuals
                  effortlessly – <b>all without the need for any design expertise.</b>
                </Typography>
              </Box>

              <Box display="flex" justifyContent="left" mt={4}>
                <Typography className={classes.graphiteColor} variant="h6">
                  Our journey has been filled with excitement, and we're thrilled to extend the
                  celebration to include all of you. The best part? While it's our party, the
                  rewards are all yours to enjoy. <b>Let’s get the 11th party started!</b>
                </Typography>
              </Box>
            </Box>
            <Box display="flex" justifyContent="center" mt={4} mb={5}>
              <NextLink
                passHref={true}
                href={{
                  pathname: '/pricing',
                  query: router.query,
                }}
              >
                <CustomButton variant="buttonSecondary">Subscribe now!</CustomButton>
              </NextLink>
            </Box>
          </Container>
        )}
      </LazyLoad>

      {/* <LazyLoad offset={100}>
        <JoinParty isJournalOrNotAvailableForPromotion={isJournalOrNotAvailableForPromotion} />
      </LazyLoad> */}

      <LazyLoad offset={100}>
        <Box className={classes.faqBox} py={6}>
          <Container>
            <Faq />
          </Container>
        </Box>
      </LazyLoad>
    </>
  );
}
